import { useEffect, useId, useRef, useState } from 'react';

import type { AgentsBoard } from '../../api/agentSchemas';
import { AGENT_FORM } from './agentLook';
import type { AgentLine } from './agentLine';
import { greeting, suggestions } from './conversation';
import { DotOrb, type DotOrbHandle } from './orb/DotOrb';
import type { OrbMood } from './orb/orbMotion';
import { useAnswer } from './useAnswer';
import { canPlayRecordedVoice, useSpeech } from './useSpeech';
import { VoicePicker } from './VoicePicker';
import { readVoicePreference, saveVoicePreference, type VoicePreference } from './voice/preference';

type AgentStageProps = {
  board: AgentsBoard;
  onReview: (proposalId: string) => void;
};

/** Long enough to see the dot dissolve and gather, short enough not to feel like waiting. */
export const THINK_MS = 900;
const ORB_SIZE = 216;
const MAX_QUESTION = 200;

/**
 * Where the owner talks to the team: the dot listens while they type, turns
 * to dust while it thinks, and answers in the voice of whichever agent owns
 * the work, taking that agent's shape. Questions about money are answered
 * with the business's own numbers; the voice says the fixed part and the
 * figures stay on the screen.
 */
export function AgentStage({ board, onReview }: AgentStageProps) {
  const orb = useRef<DotOrbHandle>(null);
  const inputId = useId();
  const [voice, setVoice] = useState<VoicePreference>(readVoicePreference);
  const { line, speech, shown, blocked, say, stop } = useSpeech(voice);
  const { canSeeMoney, reply } = useAnswer(board);
  const [question, setQuestion] = useState('');
  const [focused, setFocused] = useState(false);
  const [thinking, setThinking] = useState(false);
  const greeted = useRef(false);
  const asking = useRef(0);

  useEffect(() => {
    if (greeted.current) return;
    greeted.current = true;
    say(greeting(board, new Date()));
  }, [board, say]);

  useEffect(
    () => () => {
      asking.current += 1;
    },
    [],
  );

  const ask = (text: string) => {
    const said = text.trim();
    if (!said || thinking) return;
    setQuestion('');
    setThinking(true);
    const mine = ++asking.current;
    void Promise.all([reply(said), wait(THINK_MS)]).then(([next]) => {
      if (asking.current !== mine) return;
      setThinking(false);
      say(next);
    });
  };

  const chooseVoice = (next: VoicePreference) => {
    setVoice(next);
    saveVoicePreference(next);
    if (next === 'off') stop();
    else if (line) say(line, next);
  };

  const speaker = board.agents.find((agent) => agent.id === (line?.agentId ?? 'direccion'));
  const words = line ? line.text.split(/\s+/).filter(Boolean) : [];
  const finished = shown >= words.length;
  const linked = line?.proposalId
    ? board.proposals.find((proposal) => proposal.id === line.proposalId && proposal.status === 'pending')
    : undefined;
  const mood: OrbMood = thinking ? 'thinking' : focused && question ? 'listening' : 'idle';

  return (
    <section className="agents-stage" aria-label="Habla con tu equipo">
      {canPlayRecordedVoice() ? <VoicePicker value={voice} onChange={chooseVoice} /> : null}

      <div className="agents-stage-orb">
        <DotOrb
          ref={orb}
          size={ORB_SIZE}
          fluid
          mood={mood}
          form={AGENT_FORM[line?.agentId ?? 'direccion']}
          speech={speech}
          onDark
          label="Dale Agents"
        />
      </div>

      <div className="agents-talk">
        <p className="agents-speech" aria-hidden="true">
          {words.map((word, index) => (
            <span key={`${index}-${word}`} className="agents-word" data-shown={index < shown || undefined}>
              {word}{' '}
            </span>
          ))}
        </p>
        <p className="sr-only" aria-live="polite">
          {line ? `${speaker?.name ?? 'Tu equipo'}: ${announcement(line)}` : ''}
        </p>
        {speaker ? (
          <p className="agents-byline" data-shown={finished || undefined}>
            {speaker.name} · {speaker.title}
          </p>
        ) : null}
        {line?.note ? (
          <p className="agents-note" data-shown={finished || undefined}>
            {line.note}
          </p>
        ) : null}
        {line?.figures?.length ? (
          <ul className="agents-figures" aria-label="Cifras" data-shown={finished || undefined}>
            {line.figures.map((figure, index) => (
              <li key={`${index}-${figure.label}`} className="agents-figure" data-tone={figure.tone}>
                <span>{figure.label}</span>
                <span>{figure.value}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {blocked ? (
          <p className="agents-note" role="status" data-shown>
            Toca la voz de arriba para oírla.
          </p>
        ) : null}
        {/* The slot keeps its height, so the stage does not jump when the button comes and goes. */}
        <div className="agents-talk-action">
          {linked && finished && !thinking ? (
            <button type="button" className="btn agents-review" onClick={() => onReview(linked.id)}>
              Revisar la propuesta
            </button>
          ) : null}
        </div>
      </div>

      <div className="agents-chips" role="group" aria-label="Pregúntale a tu equipo">
        {suggestions(board, { finance: canSeeMoney }).map((chip) => (
          <button key={chip} type="button" className="agents-chip" onClick={() => ask(chip)} disabled={thinking}>
            {chip}
          </button>
        ))}
      </div>

      <form
        className="agents-ask"
        onSubmit={(event) => {
          event.preventDefault();
          ask(question);
        }}
      >
        <label className="sr-only" htmlFor={inputId}>
          Escríbele a tu equipo
        </label>
        <input
          id={inputId}
          value={question}
          maxLength={MAX_QUESTION}
          autoComplete="off"
          placeholder="Escríbele a tu equipo"
          onChange={(event) => {
            setQuestion(event.target.value);
            orb.current?.nudge();
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        <button type="submit" className="agents-send" aria-label="Enviar" disabled={!question.trim() || thinking}>
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d="M10 15.5v-11M5.5 9 10 4.5 14.5 9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </form>
    </section>
  );
}

/** What a screen reader hears: the words, the note and every figure, since they are not all spoken. */
function announcement(line: AgentLine): string {
  const figures = (line.figures ?? []).map((figure) => `${figure.label} ${figure.value}`);
  return [line.text, line.note, ...figures]
    .filter((part): part is string => Boolean(part))
    .map((part) => (/[.!?]$/.test(part) ? part : `${part}.`))
    .join(' ');
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
